import { useRef, useState } from 'react';
import { Button, Card, Modal } from '@safetyhub/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { extractApiError } from '../../../../lib/api';
import { ChevronRightIcon } from './Icons';
import { DirtyHint } from '../../../../components/common/DirtyHint';

interface ManagedItem {
  id: string;
  name: string;
  isActive: boolean;
}

interface Props<T extends ManagedItem> {
  companyId: string;
  siteId: string;
  isOwner: boolean;
  title: string;
  description: string;
  queryKey: string;
  emptyText: string;
  emptyAddText: string;
  fieldLabel: string;
  fieldPlaceholder: string;
  modalAddTitle: string;
  modalEditTitle: string;
  deactivateTitle: string;
  deactivateDescription: (name: string) => React.ReactNode;
  emptyIcon: React.ReactNode;
  fetchItems: (orgId: string, siteId: string) => Promise<T[]>;
  createItem: (orgId: string, siteId: string, dto: { name: string }) => Promise<T>;
  updateItem: (orgId: string, siteId: string, itemId: string, dto: { name?: string; isActive?: boolean }) => Promise<T>;
}

export function SiteManagedListSection<T extends ManagedItem>({
  companyId,
  siteId,
  isOwner,
  title,
  description,
  queryKey,
  emptyText,
  emptyAddText,
  fieldLabel,
  fieldPlaceholder,
  modalAddTitle,
  modalEditTitle,
  deactivateTitle,
  deactivateDescription,
  emptyIcon,
  fetchItems,
  createItem,
  updateItem,
}: Props<T>) {
  const queryClient = useQueryClient();

  const { data: items = [], isLoading } = useQuery({
    queryKey: [queryKey, companyId, siteId],
    queryFn: () => fetchItems(companyId, siteId),
    enabled: !!companyId && !!siteId,
  });

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<T | null>(null);
  const [formName, setFormName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [selectedItem, setSelectedItem] = useState<T | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<T | null>(null);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [deactivateError, setDeactivateError] = useState('');
  // 추가/수정 모달이 열릴 때 이름 입력에 포커스 — `autoFocus`는 base Modal의 마운트 포커스
  // (dialog 컨테이너)에 덮이므로 initialFocusRef로 넘긴다.
  const nameInputRef = useRef<HTMLInputElement>(null);

  const activeItems = items.filter((item) => item.isActive);

  const resetForm = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormName('');
    setError('');
  };

  const openAdd = () => {
    resetForm();
    setShowModal(true);
  };

  const openEdit = (item: T) => {
    setSelectedItem(null);
    setEditingItem(item);
    setFormName(item.name);
    setError('');
    setShowModal(true);
  };

  const closeDeactivate = () => {
    setDeactivateTarget(null);
    setDeactivateError('');
  };

  const startDeactivateFromDetail = (item: T) => {
    setSelectedItem(null);
    setDeactivateTarget(item);
  };

  // 수정 모드에서 이름이 그대로면 저장을 막는다(추가 모드는 게이트 비대상).
  // 입력이 이름 하나뿐이라 스냅샷 훅 없이 직접 비교한다.
  const isUnchanged = !!editingItem && formName.trim() === editingItem.name.trim();

  const handleSubmit = async () => {
    // 버튼 disabled를 우회하는 경로(입력 Enter)까지 무변경 저장을 막는다.
    if (!formName.trim() || isUnchanged) return;
    setIsSubmitting(true);
    setError('');
    try {
      if (editingItem) {
        await updateItem(companyId, siteId, editingItem.id, { name: formName.trim() });
      } else {
        await createItem(companyId, siteId, { name: formName.trim() });
      }
      queryClient.invalidateQueries({ queryKey: [queryKey, companyId, siteId] });
      resetForm();
    } catch (err) {
      setError(extractApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setIsDeactivating(true);
    setDeactivateError('');
    try {
      await updateItem(companyId, siteId, deactivateTarget.id, { isActive: false });
      queryClient.invalidateQueries({ queryKey: [queryKey, companyId, siteId] });
      setDeactivateTarget(null);
      setDeactivateError('');
    } catch (err) {
      setDeactivateError(extractApiError(err).message);
    } finally {
      setIsDeactivating(false);
    }
  };

  // 여백이 화면폭별로 갈려(p-4 sm:p-6) padding prop을 끄고 className으로 준다 — Card 주석의 탈출구
  return (
    <Card padding="none" radius="lg" elevation="md" className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-medium text-ink-900">{title}</h2>
        {isOwner && (
          <button onClick={openAdd} className="text-sm text-brand-600 hover:text-brand-800">
            + 추가
          </button>
        )}
      </div>
      <p className="text-xs text-ink-400 mb-4">{description}</p>

      {isLoading ? (
        <div className="flex justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600" />
        </div>
      ) : activeItems.length === 0 ? (
        <div className="text-center py-6">
          {emptyIcon}
          <p className="mt-2 text-sm text-ink-500">{emptyText}</p>
          {isOwner && (
            <button onClick={openAdd} className="mt-2 text-sm text-brand-600 hover:text-brand-800">
              {emptyAddText}
            </button>
          )}
        </div>
      ) : (
        <ul className="divide-y divide-line">
          {activeItems.map((item) => (
            <li
              key={item.id}
              className="py-3 flex items-center cursor-pointer hover:bg-surface-hover -mx-2 px-2 rounded-card transition-colors"
              onClick={() => setSelectedItem(item)}
            >
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-ink-900">{item.name}</span>
              </div>
              <ChevronRightIcon className="flex-shrink-0 h-5 w-5 text-ink-400 ml-2" />
            </li>
          ))}
        </ul>
      )}

      {/* 상세 모달 */}
      <Modal
        open={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        title={selectedItem?.name}
        size="sm"
        footer={
          selectedItem &&
          (isOwner ? (
            <>
              {/* 비활성화는 되돌릴 수 있는 경고성 액션 — 주황 의미색을 유지한다. */}
              <Button
                variant="secondary"
                onClick={() => startDeactivateFromDetail(selectedItem)}
                className="text-orange-600 hover:bg-orange-50 hover:text-orange-700"
              >
                비활성화
              </Button>
              <Button variant="primary" onClick={() => openEdit(selectedItem)}>
                수정
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={() => setSelectedItem(null)}>
              닫기
            </Button>
          ))
        }
      >
        <p className="text-sm text-ink-500">{title}</p>
      </Modal>

      {/* 추가/수정 모달 — 저장 중에는 닫는 길 전부를 막는다(종전 오버레이의 `!isSubmitting` 가드와 같은 축) */}
      <Modal
        open={showModal}
        onClose={resetForm}
        title={editingItem ? modalEditTitle : modalAddTitle}
        size="sm"
        initialFocusRef={nameInputRef}
        closeOnOverlay={!isSubmitting}
        closeOnEsc={!isSubmitting}
        showCloseButton={!isSubmitting}
        closeLocked={isSubmitting}
        footer={
          <>
            {/* 저장이 왜 막혔는지 보이지 않으면 "버튼이 죽었다"로 읽힌다 — 이유를 같이 띄운다. */}
            {editingItem && (
              <DirtyHint isDirty={!isUnchanged} className="mr-auto self-center text-sm" />
            )}
            <Button variant="secondary" onClick={resetForm} disabled={isSubmitting}>
              취소
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!formName.trim() || isSubmitting || isUnchanged}
            >
              {isSubmitting ? '저장 중...' : editingItem ? '수정' : '추가'}
            </Button>
          </>
        }
      >
        {error && (
          <div className="mb-3 rounded-card bg-red-50 p-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1">
              {fieldLabel} <span className="text-red-500">*</span>
            </label>
            <input
              ref={nameInputRef}
              type="text"
              placeholder={fieldPlaceholder}
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              className="w-full px-3 py-2 border border-line rounded-control text-sm focus:ring-brand-500 focus:border-brand-500"
            />
          </div>
        </div>
      </Modal>

      {/* 비활성화 확인 모달 — 처리 중에는 닫는 길을 막는다(종전엔 취소 버튼만 disabled였다) */}
      <Modal
        open={!!deactivateTarget}
        onClose={closeDeactivate}
        title={deactivateTitle}
        size="sm"
        closeOnOverlay={!isDeactivating}
        closeOnEsc={!isDeactivating}
        showCloseButton={!isDeactivating}
        closeLocked={isDeactivating}
        footer={
          <>
            <Button variant="secondary" onClick={closeDeactivate} disabled={isDeactivating}>
              취소
            </Button>
            {/* 비활성화 = 되돌릴 수 있는 경고성 액션 → warning(파괴적 danger와 구분). */}
            <Button variant="warning" onClick={handleDeactivate} disabled={isDeactivating}>
              {isDeactivating ? '처리 중...' : '비활성화'}
            </Button>
          </>
        }
      >
        {deactivateTarget && (
          <p className="text-sm text-ink-500 mb-4">
            {deactivateDescription(deactivateTarget.name)}
          </p>
        )}
        {deactivateError && (
          <p className="text-sm text-red-600 mb-3">{deactivateError}</p>
        )}
      </Modal>
    </Card>
  );
}
